import React, { memo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import {
  Stack,
  Icon,
  Grid,
  Card,
  TextField,
  LoadingButton,
} from "investira.react.components";
import { VALIDATIONS } from "../../../const";
import { DatePicker } from "../../../components/molecules";
import services from "../../../services";
import withResponseHandling from "../../../hoc/withResponseHandling";

const CenarioEdit = memo((props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const locationState = location.state || {};

  const VALIDATION_SCHEMA = Yup.object().shape({
    cenario: Yup.string()
      .max(128, "Máximo de 128 caracteres")
      .required(VALIDATIONS.REQUIRED),
    descricao: Yup.string()
      .max(255, "Máximo de 255 caracteres")
      .required(VALIDATIONS.REQUIRED),
    inicio: Yup.date()
      .test(
        "inicio",
        '"Data início" não pode ser maior do que a data de fim',
        function (value) {
          const { fim } = this.parent;
          if (!value || !fim) return true;
          return (
            dayjs(value).isBefore(dayjs(fim)) || dayjs(value).isSame(dayjs(fim))
          );
        }
      )
      .required(VALIDATIONS.REQUIRED),
    fim: Yup.date()
      .test(
        "fim",
        '"Data fim" não pode ser maior do que a data de início',
        function (value) {
          const { inicio } = this.parent;
          if (!value || !inicio) return true;
          return (
            dayjs(value).isAfter(dayjs(inicio)) ||
            dayjs(value).isSame(dayjs(inicio))
          );
        }
      )
      .required(VALIDATIONS.REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      cenario: locationState.cenario || "",
      descricao: locationState.descricao || "",
      inicio: locationState.inicio
        ? dayjs(locationState.inicio)
        : dayjs().add(-1, "month"),
      fim: locationState.fim ? dayjs(locationState.fim) : dayjs(),
    },
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: false,
    onSubmit: (pValues, pActions) => {
      pActions.setSubmitting(true);
      services.cenarios.modify(
        {
          cenario_id: locationState.cenario_id,
          cenario: pValues.cenario,
          descricao: pValues.descricao,
          inicio: pValues.inicio && dayjs(pValues.inicio).toISOString(),
          fim: pValues.fim && dayjs(pValues.fim).toISOString(),
        },
        (rRes) => {
          navigate(`${location.pathname.replace(/\/edit$/, "")}`);
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    },
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack spacing={2}>
            <Card>
              <Stack spacing={2} sx={{ pt: 3, pb: 2, px: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    id="cenario"
                    label="Nome do Cenário"
                    value={formik.values.cenario}
                    onChange={formik.handleChange}
                    variant="outlined"
                    placeholder="Digite um nome para o cenário"
                    fullWidth
                    disabled={props.isSubmitting}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={Boolean(
                      formik.touched.cenario && formik.errors.cenario
                    )}
                    helperText={formik.touched.cenario && formik.errors.cenario}
                  />

                  <TextField
                    id="descricao"
                    label="Descrição"
                    value={formik.values.descricao}
                    onChange={formik.handleChange}
                    variant="outlined"
                    placeholder="Digite uma descrição para o cenário"
                    fullWidth
                    multiline
                    rows={2}
                    maxRows={4}
                    disabled={props.isSubmitting}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={Boolean(
                      formik.touched.descricao && formik.errors.descricao
                    )}
                    helperText={
                      formik.touched.descricao && formik.errors.descricao
                    }
                  />
                </Stack>
                <Stack gap={2} direction={"row"}>
                  <Stack flex={1}>
                    <DatePicker
                      label="Início"
                      value={formik.values.inicio}
                      onChange={(newValue) => {
                        formik.setFieldValue("inicio", newValue);
                      }}
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          error: Boolean(formik.errors.inicio),
                          helperText: formik.errors.inicio,
                        },
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                  <Stack flex={1}>
                    <DatePicker
                      label="Fim"
                      value={formik.values.fim}
                      onChange={(newValue) => {
                        formik.setFieldValue("fim", newValue);
                      }}
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          error: Boolean(formik.errors.fim),
                          helperText: formik.errors.fim,
                        },
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </Stack>
                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    startIcon={<Icon iconName="save" size={16} />}
                    sx={{ px: 2 }}
                    type="submit"
                    disabled={formik.isSubmitting || !formik.isValid}
                    loading={formik.isSubmitting}
                  >
                    Salvar
                  </LoadingButton>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
});

export default withResponseHandling(CenarioEdit);
